import { type ComponentProps, type ReactNode, forwardRef, useId } from 'react';

import { cn } from '../lib/cn';

export interface DocFieldProps extends Omit<ComponentProps<'textarea'>, 'children'> {
  /** 칸 이름 한 줄. 지금 쓰는 칸이면 진해진다. */
  label: ReactNode;
  /** 라벨 줄 오른쪽 끝 — 이 칸에만 걸리는 동작 하나. */
  action?: ReactNode;
  /** 텍스트 영역 밖 바깥 묶음의 클래스. `className`은 텍스트 영역에 붙는다. */
  wrapperClassName?: string;
}

/**
 * 지면 위에 바로 쓰는 문서 칸 — 테두리 없는 여러 줄 입력(D3 §4.4 · D150 ⑥).
 *
 * 비면 한 줄(44px) 높이이고 내용만큼 자란다. 지금 쓰는 칸은 라벨이 `fg`로 진해지고 왼쪽에
 * 2px `selected` 선이 선다 — 고빈도라 모션 없이 바뀐다(D4-20). 테두리 입력이 필요한 폼은 `Field`다.
 */
export const DocField = forwardRef<HTMLTextAreaElement, DocFieldProps>(function DocField(
  { label, action, id, wrapperClassName, className, ...props },
  ref,
) {
  const autoId = useId();
  const controlId = id ?? autoId;

  return (
    <div className={cn('group/doc relative flex flex-col', wrapperClassName)}>
      <span
        aria-hidden="true"
        className="group-focus-within/doc:bg-selected split:-left-card absolute top-0.5 bottom-0.5 -left-2.5 w-0.5 rounded-full"
      />
      <div className="gap-snug flex min-h-6 items-center justify-between">
        <label
          htmlFor={controlId}
          className="text-ink-muted group-focus-within/doc:text-fg text-sm font-semibold"
        >
          {label}
        </label>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
      <textarea
        ref={ref}
        id={controlId}
        className={cn(
          'text-ink leading-read py-tight block min-h-11 w-full resize-none border-0 bg-transparent text-sm',
          // field-sizing이 없는 브라우저는 자라지 않으므로 세 줄 높이에서 스크롤한다.
          'field-sizing-content focus:outline-none supports-[not(field-sizing:content)]:min-h-24',
          'placeholder:text-ink-faint',
          'disabled:cursor-not-allowed disabled:opacity-60',
          className,
        )}
        {...props}
      />
    </div>
  );
});
